import { Controller } from "@hotwired/stimulus"

// Turbo Drive is disabled for the workspace, but Turbo frames and forms are
// still used. Cover full-page navigation without obscuring in-place updates.
export default class extends Controller {
  static targets = ["overlay"]

  connect() {
    this.onClick = this.handleClick.bind(this)
    this.onSubmit = this.handleSubmit.bind(this)
    this.onTurboSubmitStart = this.handleTurboSubmitStart.bind(this)
    this.onTurboSubmitEnd = this.handleTurboSubmitEnd.bind(this)
    this.onNavigate = this.schedule.bind(this)
    this.onHide = this.hide.bind(this)

    document.addEventListener("click", this.onClick)
    document.addEventListener("submit", this.onSubmit)
    document.addEventListener("turbo:submit-start", this.onTurboSubmitStart)
    document.addEventListener("turbo:submit-end", this.onTurboSubmitEnd)
    document.addEventListener("turbo:before-visit", this.onNavigate)
    document.addEventListener("turbo:load", this.onHide)
    document.addEventListener("turbo:fetch-request-error", this.onHide)
    window.addEventListener("pageshow", this.onHide)
  }

  disconnect() {
    document.removeEventListener("click", this.onClick)
    document.removeEventListener("submit", this.onSubmit)
    document.removeEventListener("turbo:submit-start", this.onTurboSubmitStart)
    document.removeEventListener("turbo:submit-end", this.onTurboSubmitEnd)
    document.removeEventListener("turbo:before-visit", this.onNavigate)
    document.removeEventListener("turbo:load", this.onHide)
    document.removeEventListener("turbo:fetch-request-error", this.onHide)
    window.removeEventListener("pageshow", this.onHide)
    this.hide()
  }

  handleClick(event) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

    const link = event.target.closest("a[href]")
    if (!link || link.hasAttribute("download") || link.dataset.loadingOverlay === "ignore") return
    if (link.target && !["_self", "_top"].includes(link.target)) return
    if (this.targetsFrame(link)) return

    const url = new URL(link.href)
    if (url.origin !== window.location.origin || !["http:", "https:"].includes(url.protocol)) return
    if (/\.(pdf|csv|xlsx|xml|zip|docx)$/i.test(url.pathname)) return
    if (url.pathname === window.location.pathname && url.search === window.location.search) return

    this.schedule(event)
  }

  handleSubmit(event) {
    const form = event.target
    const target = event.submitter?.formTarget || form.target
    if (target && !["_self", "_top"].includes(target)) return
    if (form.method === "dialog" || form.dataset.loadingOverlay === "ignore" || this.targetsFrame(form, event.submitter)) return

    this.schedule(event)
  }

  handleTurboSubmitStart(event) {
    if (!this.targetsFrame(event.target, event.detail.formSubmission?.submitter)) this.schedule()
  }

  handleTurboSubmitEnd(event) {
    if (!this.targetsFrame(event.target, event.detail.formSubmission?.submitter)) this.hide()
  }

  targetsFrame(element, submitter) {
    const frame = element.closest("turbo-frame")
    const frameTarget = submitter?.dataset.turboFrame || element.dataset.turboFrame || frame?.getAttribute("target") || frame?.id
    return frameTarget && frameTarget !== "_top"
  }

  schedule(event) {
    clearTimeout(this.timer)
    this.timer = setTimeout(() => {
      if (!event?.defaultPrevented) this.show()
    }, 180)
  }

  show() {
    this.overlayTarget.hidden = false
    document.getElementById("main-content")?.setAttribute("aria-busy", "true")
  }

  hide() {
    clearTimeout(this.timer)
    this.overlayTarget.hidden = true
    document.getElementById("main-content")?.removeAttribute("aria-busy")
  }
}
